import type {PackageType} from '@sollapay/enums';
import type {HttpClient} from '@sollapay/http-client';
import type {
  ActivateTrustAccountResponseDTO,
  ApiResponse,
  TrustScopeUpsertPayload,
  TrustScopeUpsertDTO,
  UpsertTrustAssetPayload,
  TrustAssetUpsertDTO,
  UpsertSellersPayload,
  TrustSellerDetailsUpsertDTO,
  SignMainFatcaCrsPayload,
  SignSupplementaryFatcaCrsPayload,
  FatcaCrsUpsertDTO,
  TrustDetailsPackageDTO,
  CompliancePackageDTO,
  SubmitPackageDTO
} from '@sollapay/types';

/**
 * API client functions for trust account activation
 */
export const activationApi = {
  upsertScope: async (
    client: HttpClient,
    id: string,
    payload: TrustScopeUpsertPayload
  ): Promise<ApiResponse<TrustScopeUpsertDTO>> => {
    return client.put<ApiResponse<TrustScopeUpsertDTO>, TrustScopeUpsertPayload>(
      `/trust_accounts/${id}/scope`,
      payload
    );
  },

  /**
   * Upsert the trust account's one shared asset (property/land + reference price)
   */
  upsertAsset: async (
    client: HttpClient,
    id: string,
    payload: UpsertTrustAssetPayload
  ): Promise<ApiResponse<TrustAssetUpsertDTO>> => {
    return client.put<ApiResponse<TrustAssetUpsertDTO>, UpsertTrustAssetPayload>(
      `/trust_accounts/${id}/asset`,
      payload
    );
  },

  /**
   * Upsert the trust account's selling-party roster
   */
  upsertSellers: async (
    client: HttpClient,
    id: string,
    payload: UpsertSellersPayload
  ): Promise<ApiResponse<TrustSellerDetailsUpsertDTO>> => {
    return client.put<ApiResponse<TrustSellerDetailsUpsertDTO>, UpsertSellersPayload>(
      `/trust_accounts/${id}/sellers`,
      payload
    );
  },

  /**
   * Sign the main FATCA/CRS declaration for a trust account
   */
  signMainFatcaCrs: async (
    client: HttpClient,
    id: string,
    payload: SignMainFatcaCrsPayload
  ): Promise<ApiResponse<FatcaCrsUpsertDTO>> => {
    return client.post<ApiResponse<FatcaCrsUpsertDTO>, SignMainFatcaCrsPayload>(
      `/trust_accounts/${id}/fatca-crs/declarations`,
      payload
    );
  },

  /**
   * Sign a supplementary FATCA/CRS declaration for a trust account
   */
  signSupplementaryFatcaCrs: async (
    client: HttpClient,
    id: string,
    payload: SignSupplementaryFatcaCrsPayload
  ): Promise<ApiResponse<FatcaCrsUpsertDTO>> => {
    return client.post<ApiResponse<FatcaCrsUpsertDTO>, SignSupplementaryFatcaCrsPayload>(
      `/trust_accounts/${id}/fatca-crs/declarations/supplementary`,
      payload
    );
  },

  /**
   * Get trust details package (scope + developer + documents)
   */
  getTrustDetailsPackage: async (
    client: HttpClient,
    id: string
  ): Promise<ApiResponse<TrustDetailsPackageDTO>> => {
    return client.get<ApiResponse<TrustDetailsPackageDTO>>(
      `/trust_accounts/${id}/packages/trust-details`
    );
  },

  /**
   * Get compliance package (fatcaCrs + legal documents)
   */
  getCompliancePackage: async (
    client: HttpClient,
    id: string
  ): Promise<ApiResponse<CompliancePackageDTO>> => {
    return client.get<ApiResponse<CompliancePackageDTO>>(
      `/trust_accounts/${id}/packages/compliance`
    );
  },

  /**
   * Submit a trust account for review (transitions to submitted_for_review)
   */
  submitTrustAccount: async (
    client: HttpClient,
    id: string
  ): Promise<ApiResponse<ActivateTrustAccountResponseDTO>> => {
    return client.post<ApiResponse<ActivateTrustAccountResponseDTO>, undefined>(
      `/trust_accounts/${id}/submit`,
      undefined
    );
  },

  /**
   * Send one package that ops returned for clarification back to review
   */
  resubmitPackage: async (
    client: HttpClient,
    id: string,
    packageType: PackageType
  ): Promise<ApiResponse<SubmitPackageDTO>> => {
    return client.post<ApiResponse<SubmitPackageDTO>, undefined>(
      `/trust_accounts/${id}/packages/${packageType}/resubmit`,
      undefined
    );
  }
};
